<header class="flex header">
  <h2>{{ 'CashRequest_Picker_Title' | translateWF: 'Plan documents' }}</h2>
  <p-button icon="pi pi-times" (click)="onClose()" />
</header>

<section class="content">
  <loading-overlay [isLoading]="isLoading() > 0"></loading-overlay>

  <!-- Filters act on what is already loaded — only "include already planned" costs a round trip, because
       the server leaves those rows out entirely. -->
  <div class="grid-12 mb-4">
    <div class="span-12-4">
      <p-iconfield>
        <p-inputicon class="pi pi-search" />
        <input
          id="picker-search"
          pInputText
          class="w-full"
          [formControl]="searchControl"
          [placeholder]="'CashRequest_Picker_SearchHint' | translateWF: 'Search request #, AP document #, fiscal'"
        />
      </p-iconfield>
    </div>
    <div class="span-12-3">
      <p-select
        inputId="picker-provider"
        [formControl]="providerControl"
        [options]="providerOptions()"
        optionLabel="label"
        optionValue="value"
        [showClear]="true"
        [filter]="true"
        filterBy="label"
        appendTo="body"
        styleClass="w-full"
        [placeholder]="'CashRequest_Picker_AllProviders' | translateWF: 'All providers'"
      />
    </div>
    <div class="span-12-3">
      <p-select
        inputId="picker-gl"
        [formControl]="glAccountControl"
        [options]="glAccountOptions()"
        optionLabel="label"
        optionValue="value"
        [showClear]="true"
        [filter]="true"
        filterBy="label"
        appendTo="body"
        styleClass="w-full"
        [placeholder]="'CashRequest_Picker_AllAccounts' | translateWF: 'All GL accounts'"
      />
    </div>
    <div class="span-12-2 row-start-center gap-3">
      <div class="row-sm-start-center">
        <p-checkbox inputId="picker-include-planned" [formControl]="includePlannedControl" [binary]="true" (onChange)="load()" />
        <label for="picker-include-planned" class="cursor-pointer text-sm">
          {{ 'CashRequest_Picker_IncludePlanned' | translateWF: 'Planned elsewhere' }}
        </label>
      </div>
      @if (hasFilters()) {
        <p-button icon="pi pi-filter-slash" [text]="true" [pTooltip]="'Clear' | translateWF: 'Clear'" (click)="clearFilters()" />
      }
    </div>
  </div>

  <p-tabs [value]="activeTab()" (valueChange)="activeTab.set($any($event))">
    <p-tablist>
      <p-tab value="requests"> {{ 'CashRequest_Picker_PaymentRequests' | translateWF: 'Payment requests' }} ({{ filteredPaymentRequests().length }}) </p-tab>
      <p-tab value="withholdings"> {{ 'CashRequest_Picker_Withholdings' | translateWF: 'Withholdings' }} ({{ filteredWithholdings().length }}) </p-tab>
      <p-tab value="advances"> {{ 'CashRequest_Picker_AdvanceRequests' | translateWF: 'Advance requests' }} ({{ filteredAdvanceRequests().length }}) </p-tab>
    </p-tablist>

    <p-tabpanels>
      <p-tabpanel value="requests">
        <ng-container *ngTemplateOutlet="candidateGroups; context: { $implicit: paymentRequestGroups(), kind: 'requests' }" />
      </p-tabpanel>
      <p-tabpanel value="withholdings">
        <ng-container *ngTemplateOutlet="candidateGroups; context: { $implicit: withholdingGroups(), kind: 'withholdings' }" />
      </p-tabpanel>
      <!-- Approved vendor advances (#6323). The "Planned elsewhere" toggle does not reach them: an advance planned
           into another cash request is never offered again, so the note says why one might be missing. -->
      <p-tabpanel value="advances">
        <p-message
          severity="info"
          styleClass="mb-3"
          [text]="
            'CashRequest_Picker_AdvancesNote' | translateWF: 'Only approved advance requests are listed. An advance already planned in another cash request is not offered again.'
          "
        />
        <ng-container *ngTemplateOutlet="candidateGroups; context: { $implicit: advanceRequestGroups(), kind: 'advances' }" />
      </p-tabpanel>
    </p-tabpanels>
  </p-tabs>

  <!-- ─────────────────────── One layout for all three tabs ───────────────────────
       They differ only in the status enum, the line under the number and what the row is made of (invoices,
       or purchase orders for an advance), so a single layout keeps them from drifting apart. Each provider is
       its own card: the provider band is the table header, its requests are the rows. -->
  <ng-template #candidateGroups let-groups let-kind="kind">
    <div class="flex flex-col gap-4">
      @for (group of groups; track group.reference) {
        <div class="overflow-hidden rounded-lg border border-[var(--p-content-border-color)]">
          <!-- Provider band: who it is, how many rows, and what planning the rest of them would still add. A
               heading over the card rather than the table's thead, which the theme paints as a column header. -->
          <div class="flex items-center gap-4 border-b border-[var(--p-content-border-color)] bg-[var(--p-content-hover-background)] px-4 py-2">
            <p-checkbox [binary]="true" [ngModel]="groupSelected(group)" (onChange)="toggleGroup(group)" />
            <div class="flex min-w-0 flex-1 items-center gap-3">
              <span class="shrink-0 whitespace-nowrap text-sm text-[var(--p-text-muted-color)]">
                {{ group.code || '—' }} · {{ group.rows.length }}
                @switch (kind) {
                  @case ('withholdings') {
                    {{ 'CashRequest_Picker_WithholdingCount' | translateWF: 'withholding(s)' }}
                  }
                  @case ('advances') {
                    {{ 'CashRequest_Picker_AdvanceCount' | translateWF: 'advance(s)' }}
                  }
                  @default {
                    {{ 'CashRequest_Picker_RequestCount' | translateWF: 'request(s)' }}
                  }
                }
              </span>
              <span class="truncate text-base font-semibold">{{ group.name || '—' }}</span>
            </div>
            <span class="text-base font-semibold tabular-nums" [pTooltip]="'CashRequest_Picker_StillAddable' | translateWF: 'still to add'" tooltipPosition="left">
              {{ groupTotal(group) | number: '1.2-2' }} USD
            </span>
          </div>

          <p-table [value]="group.rows" dataKey="sourceDocument.reference" [expandedRowKeys]="expandedRows()" [rowHover]="true" size="small">
            <ng-template pTemplate="body" let-row>
              <tr class="cursor-pointer" (click)="toggle(row)">
                <td style="width: 3.5rem" (click)="$event.stopPropagation()">
                  <p-checkbox [binary]="true" [ngModel]="isSelected(row)" (onChange)="toggle(row)" />
                </td>

                <!-- Identity + who raised it + where it already sits in the cash plan. -->
                <td style="width: 24rem">
                  <div class="flex flex-col items-start gap-0.5">
                    <div class="flex flex-wrap items-center gap-2">
                      <span class="font-mono font-semibold">{{ row.sourceDocument.number || '—' }}</span>
                      @if (kind === 'withholdings') {
                        <p-tag
                          styleClass="uppercase tracking-wide text-xs"
                          [severity]="withholdingStatusSeverity(row.status)"
                          [value]="withholdingStatusLabel(row.status) | translateWF: withholdingStatusLabel(row.status)"
                        />
                      } @else {
                        <p-tag
                          styleClass="uppercase tracking-wide text-xs"
                          [severity]="paymentRequestStatusSeverity(row.status)"
                          [value]="paymentRequestStatusLabel(row.status) | translateWF: paymentRequestStatusLabel(row.status)"
                        />
                      }
                      @if (row.alreadyInThisRequest) {
                        <p-tag styleClass="uppercase tracking-wide text-xs" severity="success" [value]="'CashRequest_Picker_Added' | translateWF: 'Added'" />
                      }
                      <!-- Covered in full by an Anticipo: still offered, because a request that vanished reads as
                           an omission — but it costs the week nothing, and the badge is what says so. -->
                      @if (isFullyAdvanceSettled(row)) {
                        <p-tag styleClass="uppercase tracking-wide text-xs" severity="info" [value]="'CashRequest_Picker_AdvanceSettled' | translateWF: 'Settled by advance'" />
                      }
                    </div>

                    @if (kind !== 'withholdings' && row.requestedBy) {
                      <span class="text-sm text-[var(--p-text-muted-color)]"> {{ 'CashRequest_Picker_RequestedBy' | translateWF: 'Requested by' }} {{ row.requestedBy }} </span>
                    }
                    @if (kind === 'withholdings' && row.certificateNumber) {
                      <span class="text-sm text-[var(--p-text-muted-color)]"> {{ 'CashRequest_Picker_Certificate' | translateWF: 'Constancia' }} {{ row.certificateNumber }} </span>
                    }

                    @if (row.inAnotherCashRequest) {
                      <p-tag
                        severity="warn"
                        icon="pi pi-clock"
                        [rounded]="true"
                        [value]="('CashRequest_Picker_Elsewhere' | translateWF: 'Planned elsewhere') + (otherRequests(row) ? ' · ' + otherRequests(row) : '')"
                      />
                    } @else if (!row.alreadyInThisRequest) {
                      <p-tag severity="warn" icon="pi pi-info-circle" [rounded]="true" [value]="'CashRequest_Picker_NotPlanned' | translateWF: 'Not in a cash plan'" />
                    }
                  </div>
                </td>

                <!-- The first document the row is made of; the rest open underneath it. The eye opens one
                     read-only, in place. -->
                <td style="width: 17rem" (click)="$event.stopPropagation()">
                  <div class="flex flex-col items-start">
                    @if (kind === 'advances') {
                      <!-- An advance is paid ahead of any invoice: what it is for is the POs it was raised against. -->
                      @if (purchaseOrdersOf(row)[0]; as po) {
                        <span class="font-medium">{{ po.number || '—' }}</span>
                        @if (purchaseOrdersOf(row).length > 1) {
                          <p-button
                            [text]="true"
                            size="small"
                            styleClass="px-0"
                            [icon]="isExpanded(row) ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                            [label]="
                              isExpanded(row)
                                ? ('CashRequest_Picker_HidePurchaseOrders' | translateWF: 'Hide purchase orders')
                                : '+' + (purchaseOrdersOf(row).length - 1) + ' ' + ('CashRequest_Picker_MorePurchaseOrders' | translateWF: 'more purchase orders')
                            "
                            (click)="toggleExpanded(row)"
                          />
                        }
                      } @else {
                        <span class="text-[var(--p-text-muted-color)]">—</span>
                      }
                    } @else {
                      @if (documentsOf(row)[0]; as document) {
                        <div class="flex items-center gap-1">
                          <span class="font-medium">{{ document.number || '—' }}</span>
                          <p-button
                            icon="pi pi-eye"
                            [text]="true"
                            [rounded]="true"
                            size="small"
                            severity="secondary"
                            [pTooltip]="'ApDocument_QuickView_Open' | translateWF: 'View document detail'"
                            (click)="openDocumentQuickView(document.reference)"
                          />
                        </div>
                        <span class="text-sm text-[var(--p-text-muted-color)]"> {{ 'ApDocument_Fiscal' | translateWF: 'Fiscal' }} {{ document.fiscalNumber || '—' }} </span>
                        @if (documentsOf(row).length > 1) {
                          <p-button
                            [text]="true"
                            size="small"
                            styleClass="px-0"
                            [icon]="isExpanded(row) ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                            [label]="
                              isExpanded(row)
                                ? ('CashRequest_Picker_HideDocuments' | translateWF: 'Hide AP documents')
                                : '+' + (documentsOf(row).length - 1) + ' ' + ('CashRequest_Picker_MoreDocuments' | translateWF: 'more AP documents')
                            "
                            (click)="toggleExpanded(row)"
                          />
                        }
                      } @else {
                        <span class="text-[var(--p-text-muted-color)]">—</span>
                      }
                    }
                  </div>
                </td>

                <td style="width: 16rem">
                  <ng-container *ngTemplateOutlet="accountChips; context: { $implicit: row }" />
                </td>

                <td style="width: 7rem" class="text-[var(--p-text-muted-color)]">{{ row.documentDate | date: 'dd/MM/yyyy' }}</td>

                <td class="text-right">
                  <div class="col">
                    <span class="text-base font-semibold tabular-nums">{{ row.sourceAmount | number: '1.2-2' }} {{ currencyLabel(row.currency) }}</span>
                    <!-- The USD figure is what the request actually plans; only worth repeating when it differs. -->
                    @if (row.functionalAmount !== row.sourceAmount) {
                      <small class="text-[var(--p-text-muted-color)] tabular-nums">≈ {{ row.functionalAmount | number: '1.2-2' }} USD</small>
                    }
                    <!-- The amount above is CASH, so an earmarked advance is already out of it. Said out loud
                         because otherwise the row is simply less than the invoices it names, and the planner has
                         no way to tell a drawdown from a mistake. -->
                    @if (advanceApplied(row) > 0) {
                      <small class="text-[var(--p-text-muted-color)] tabular-nums">
                        − {{ advanceApplied(row) | number: '1.2-2' }} {{ currencyLabel(row.currency) }}
                        {{ 'CashRequest_Picker_AdvanceApplied' | translateWF: 'advance applied' }}
                      </small>
                    }
                  </div>
                </td>
              </tr>
            </ng-template>

            <!-- Every document behind the row, opened from "+N more" under its first one. -->
            <ng-template pTemplate="rowexpansion" let-row>
              <tr>
                <td colspan="6" class="bg-[var(--p-content-hover-background)]">
                  <div class="py-2 pl-14">
                    <div class="overflow-hidden rounded-lg border border-[var(--p-content-border-color)]">
                      @if (kind === 'advances') {
                        <p-table [value]="purchaseOrdersOf(row)" dataKey="reference" [rowHover]="true" size="small">
                          <ng-template pTemplate="header">
                            <tr>
                              <th>{{ 'CashRequest_Picker_PurchaseOrders' | translateWF: 'Purchase orders' }}</th>
                              <th class="text-right">{{ 'CashRequest_Picker_SourceAmount' | translateWF: 'Amount' }}</th>
                            </tr>
                          </ng-template>
                          <ng-template pTemplate="body" let-po>
                            <tr>
                              <td>{{ po.number || '—' }}</td>
                              <td class="text-right font-semibold tabular-nums">{{ po.amount | number: '1.2-2' }} {{ currencyLabel(row.currency) }}</td>
                            </tr>
                          </ng-template>
                        </p-table>
                      } @else {
                        <p-table [value]="documentsOf(row)" dataKey="reference" [rowHover]="true" size="small">
                          <ng-template pTemplate="header">
                            <tr>
                              <th>{{ 'CashRequest_Picker_Documents' | translateWF: 'AP documents' }}</th>
                              <th>{{ 'ApDocument_Fiscal' | translateWF: 'Fiscal' }}</th>
                              <th class="text-right">{{ 'CashRequest_Picker_SourceAmount' | translateWF: 'Amount' }}</th>
                              <th style="width: 4rem"></th>
                            </tr>
                          </ng-template>
                          <ng-template pTemplate="body" let-document>
                            <tr>
                              <td>{{ document.number || '—' }}</td>
                              <td>{{ document.fiscalNumber || '—' }}</td>
                              <td class="text-right font-semibold tabular-nums">
                                @if (document.amount !== null) {
                                  {{ document.amount | number: '1.2-2' }} {{ currencyLabel(document.currency) }}
                                } @else {
                                  —
                                }
                              </td>
                              <td class="text-right">
                                <p-button
                                  icon="pi pi-eye"
                                  [text]="true"
                                  [rounded]="true"
                                  size="small"
                                  severity="secondary"
                                  [pTooltip]="'ApDocument_QuickView_Open' | translateWF: 'View document detail'"
                                  (click)="openDocumentQuickView(document.reference)"
                                />
                              </td>
                            </tr>
                          </ng-template>
                        </p-table>
                      }
                    </div>
                  </div>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>
      } @empty {
        <div class="py-6 text-center text-[var(--p-text-muted-color)]">
          {{ 'CashRequest_Picker_None' | translateWF: 'Nothing available to plan.' }}
        </div>
      }
    </div>
  </ng-template>

  <!-- One chip per GL account the candidate would land on. More than one means it fans out into one entry
       per account, which is what the planner needs to see before ticking it. The first is the account the
       row is paid from, so it is the one tinted. -->
  <ng-template #accountChips let-row>
    @if (row.accounts.length) {
      <div class="flex flex-col gap-1">
        @for (account of row.accounts.slice(0, 2); track account.reference; let first = $first) {
          <div
            class="flex items-baseline gap-2 rounded-md border border-[var(--p-content-border-color)] px-2 py-0.5"
            [ngClass]="{ 'bg-[var(--p-primary-50)]': first }"
            [title]="accountName(account)"
          >
            <span class="font-mono text-sm font-semibold">{{ account.number }}</span>
            <span class="truncate text-sm text-[var(--p-text-muted-color)]">{{ accountName(account) }}</span>
          </div>
        }
        @if (row.accounts.length > 2) {
          <span class="text-xs text-[var(--p-text-muted-color)]">+{{ row.accounts.length - 2 }} {{ 'More' | translateWF: 'more' }}</span>
        }
      </div>
    } @else {
      <span class="text-[var(--p-text-muted-color)]">—</span>
    }
  </ng-template>
</section>

<footer>
  <div class="row-between-center w-full gap-4">
    <div class="col text-sm">
      <span>
        {{ 'CashRequest_Picker_Selected' | translateWF: 'Selected' }}:
        <strong>{{ selectedCount() }}</strong>
        · <strong class="tabular-nums">{{ selectedTotal() | number: '1.2-2' }}</strong> USD
      </span>
      @if (hasChanges()) {
        <span class="text-xs text-[var(--p-text-muted-color)]">
          @if (addCount()) {
            {{ 'CashRequest_Picker_ToAdd' | translateWF: 'to add' }}: {{ addCount() }}
          }
          @if (addCount() && removeCount()) {
            ·
          }
          @if (removeCount()) {
            {{ 'CashRequest_Picker_ToRemove' | translateWF: 'to remove' }}: {{ removeCount() }}
          }
        </span>
      }
    </div>
    <div class="row-sm">
      <p-button [label]="'Cancel' | translateWF: 'Cancel'" variant="outlined" (click)="onClose()" />
      <p-button [label]="'Apply' | translateWF: 'Apply'" icon="pi pi-check" [disabled]="!hasChanges()" (click)="onSave()" />
    </div>
  </div>
</footer>
